import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { PartnerDto, PartnerInput, PartnerOptionsDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf, referencesText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "./common";
import { AccName, accText } from "../accounting/common";
import { amt } from "../ledger/common";

// General accounts › Partners, ported from the prototype (erp-accounts.js "/partners"): title, mobile and account
// index, and each partner's balance. Rules G-1 to G-3 are provisional (docs/proposed-financial-rules.md, section 12):
// the account is an Owner's Equity account; journal lines, deposits and withdrawals on it may name the partner, and
// the balance is what those posted lines add up to (credit minus debit: what the business owes the partner).

export const PartnerProvisional = () => { const { t } = useLang(); return <div className="dep-note" data-testid="partner-provisional"><Icon name="info" /><span>{t("partnerProvisional")}</span></div>; };

function PartnerForm({ p, opts, onClose, onSaved }: { p: PartnerDto | null; opts: PartnerOptionsDto | null; onClose(): void; onSaved(x: PartnerDto, created: boolean): void }) {
  const { t, L } = useLang();
  const [v, setV] = useState<PartnerInput>(() => (p ? { name: p.name, phone: p.phone, accountId: p.account.id } : { name: "", phone: "", accountId: "" }));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof PartnerInput, x: string) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };
  const tagged = !!p && p.linesCount + p.vouchersCount > 0;
  async function save() {
    const e: Record<string, string> = {};
    if (!v.name.trim()) e.name = t("requiredField");
    if (v.phone.trim() && !/^05\d{8}$/.test(v.phone.trim())) e.phone = t("mobileFormat");
    if (!v.accountId) e.accountId = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(Object.values(e)[0]!); return; }
    setBusy(true); setAlert(null);
    try { onSaved(p ? await api<PartnerDto>("PUT", `/partners/${p.id}`, v) : await api<PartnerDto>("POST", "/partners", v), !p); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  return (
    <Modal title={t(p ? "editPartner" : "newPartner")} onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <Field label={t("partnerTitle")} req full error={errs.name} htmlFor="pt-name"><input id="pt-name" className="control" value={v.name} maxLength={200} onChange={(e) => set("name", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("phone")} error={errs.phone} htmlFor="pt-phone"><input id="pt-phone" className="control num" style={{ textAlign: "start" }} dir="ltr" type="tel" placeholder="05XXXXXXXX" value={v.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("accountIndex")} req error={errs.accountId} htmlFor="pt-acc" hint={tagged ? t("partnerAccountLocked") : t("partnerAccountHintForm")}>
        <select id="pt-acc" className="control" value={v.accountId} disabled={!opts || tagged} onChange={(e) => set("accountId", e.target.value)}>
          <option value="">{t("choose")}</option>{(opts?.accounts ?? []).map((a) => <option key={a.id} value={a.id}>{a.code} {L(a)}</option>)}
        </select>
      </Field>
    </Modal>
  );
}

export function Partners() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const [rows, setRows] = useState<PartnerDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [opts, setOpts] = useState<PartnerOptionsDto | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<PartnerDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<PartnerDto | null>(null);
  const [blocked, setBlocked] = useState<PartnerDto | null>(null);
  useEffect(() => { api<PartnerOptionsDto>("GET", "/partners/options").then(setOpts, () => setOpts(null)); }, []);
  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (filters.phone) p.set("phone", filters.phone);
    try { setRows(await api<PartnerDto[]>("GET", `/partners${p.size ? "?" + p : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.phone, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(p: PartnerDto) {
    try { await api("DELETE", `/partners/${p.id}`); toast(t("deletedOk", { x: p.name })); } catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const columns: Column<PartnerDto>[] = [
    { k: "name", label: t("partnerName"), cell: (p) => <b>{p.name}</b>, csv: (p) => p.name },
    { k: "phone", label: t("phone"), cell: (p) => (p.phone ? <span className="num" dir="ltr">{p.phone}</span> : <span className="muted">–</span>), csv: (p) => p.phone },
    { k: "acc", label: t("accountIndex"), cell: (p) => (s.can("chartOfAccountLevel3s") ? <Link to={`/account/${p.account.id}`}><AccName a={p.account} /></Link> : <AccName a={p.account} />), csv: (p) => accText(L, p.account) },
    { k: "bal", label: t("balance"), end: true, cell: (p) => <span data-testid="partner-balance">{amt(p.balance)}</span>, csv: (p) => amt(p.balance) }
  ];
  const newBtn = s.can("partnerAdd") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newPartner")}</button>
    : <LockedButton label={t("newPartner")} title={t("noPermissionFor", { p: "partnerAdd" })} />;
  return (
    <Shell navKey="/partners">
      <PageHead crumbs={[[t("home"), "/home"], [t("generalAccounts")], [t("partners")]]} title={t("partners")} />
      <PartnerProvisional />
      <ListView rows={rows} error={failed} columns={columns} file="partners" testId="partners-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("partnerName"), type: "text" }, { k: "phone", label: t("phone"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(p) => `${p.name} ${p.phone} ${p.account.code}`} newButton={newBtn}
        empty={{ icon: "users", title: t("noResults"), body: t("noResultsBody") }}
        actions={(p) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(p), ...(s.can("partnerUpdate") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (p.linesCount + p.vouchersCount ? setBlocked(p) : setDeleting(p)), ...(s.can("partnerDelete") ? {} : lockTag(t)) }
        ]} />
      {editing && <PartnerForm p={editing === "new" ? null : editing} opts={opts} onClose={() => setEditing(null)}
        onSaved={(p, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: p.name })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const p = deleting; setDeleting(null); void remove(p); }} />}
      {blocked && <Blocked title={t("cantDelete")}
        body={`${t("cantDeletePartner")} (${referencesText(t, { ...(blocked.linesCount ? { journalLines: blocked.linesCount } : {}), ...(blocked.vouchersCount ? { vouchers: blocked.vouchersCount } : {}) })})`}
        onClose={() => setBlocked(null)} />}
    </Shell>
  );
}
